import React, { useEffect, useState } from "react";
import BsModal from "../../Common/BsModal";
import Notification from "../../Common/Notification";
import ActivityLoader from "../../Common/ActivityLoader";
import RenderSecure from "../../../util/RenderSecure";
import ajaxSchool from "../../../util/remote/ajaxSchool";
import { numberFormat } from "../../../util/araknerdlib";

const Section = ({ title, children }) => (
  <div className="mb-3">
    <div className="font-weight-semibold mb-2">{title}</div>
    {children}
  </div>
);

const SimpleTable = ({ columns, rows, empty }) =>
  Array.isArray(rows) && rows.length > 0 ? (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr>{columns.map((c) => <th key={c.label} className={c.right ? "text-right" : ""}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.label} className={c.right ? "text-right" : ""}>
                  {c.money ? numberFormat(row[c.key]) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <small className="text-muted">{empty}</small>
  );

const AdvanceReconcileModal = ({ show, onClose, studentId, onReconciled }) => {
  const [preview, setPreview] = useState(null);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && studentId) {
      getPreview();
    } else {
      setPreview(null);
      setNotify(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, studentId]);

  const getPreview = async () => {
    setPreview(null);
    const server_response = await ajaxSchool.fetchAdvanceFeesPreview(studentId);
    if (server_response.status === "OK") {
      setPreview(server_response.details);
    } else {
      setPreview("404");
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  const handleReconcile = async () => {
    if (loading) return;
    setLoading(true);
    const server_response = await ajaxSchool.reconcileAdvanceFees(studentId);
    setLoading(false);
    if (server_response.status === "OK") {
      setNotify(<Notification type="success" message={server_response.message} />);
      getPreview();
      onReconciled && onReconciled();
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  const proposed = preview?.proposed_allocations || [];

  const footer = (
    <>
      <button className="btn btn-light" onClick={onClose}>Close</button>
      <RenderSecure code={["STUDENT_FEES_ADVANCE"]}>
        <button
          className="btn btn-primary"
          onClick={handleReconcile}
          disabled={loading || !preview || preview === "404" || proposed.length === 0}
        >
          <i className="fa fa-check-circle mr-2"></i>
          {loading ? "Reconciling..." : "Reconcile advance"}
        </button>
      </RenderSecure>
    </>
  );

  return (
    <BsModal show={show} onClose={onClose} title="Advance Fees Reconciliation" footer={footer} size="modal-lg">
      {notify}
      {!preview && <ActivityLoader />}
      {preview && preview !== "404" && (
        <>
          <div className="mb-3">
            <div className="font-weight-bold">{preview.student?.full_name}</div>
            <small className="text-muted">
              {preview.student?.reg_no} · {preview.student?.class?.class_name || ""}
            </small>
          </div>

          <div className="d-flex justify-content-between mb-3 flex-wrap">
            <div><small className="text-muted">Advance total</small><div>UGX {numberFormat(preview.advance_total)}</div></div>
            <div><small className="text-muted">Allocated</small><div>UGX {numberFormat(preview.allocated)}</div></div>
            <div><small className="text-muted">Unallocated</small><div className="text-success font-weight-bold">UGX {numberFormat(preview.unallocated)}</div></div>
          </div>

          <Section title="Advance payments">
            <SimpleTable
              rows={preview.advances}
              empty="No advance payments."
              columns={[
                { label: "Date", key: "transaction_date" },
                { label: "Reference", key: "reference" },
                { label: "Amount", key: "amount", money: true, right: true },
              ]}
            />
          </Section>

          <Section title="Overpaid fee votes">
            <SimpleTable
              rows={preview.overpaid_votes}
              empty="No overpaid votes."
              columns={[
                { label: "Fee item", key: "fee_name" },
                { label: "Billed", key: "billed", money: true, right: true },
                { label: "Paid", key: "paid", money: true, right: true },
                { label: "Overpaid", key: "overpaid", money: true, right: true },
              ]}
            />
          </Section>

          <Section title="To be applied on reconcile">
            <SimpleTable
              rows={proposed}
              empty="Nothing to apply: no unallocated advance or no outstanding vote."
              columns={[
                { label: "Fee item", key: "fee_name" },
                { label: "Invoice date", key: "transaction_date" },
                { label: "Amount", key: "amount", money: true, right: true },
              ]}
            />
            {proposed.length > 0 && (
              <small className="text-muted d-block mt-1">
                <i className="fa fa-info-circle mr-1"></i>
                Each entry is posted on its invoice date, since the advance was already received.
              </small>
            )}
          </Section>

          <Section title="Already applied">
            <SimpleTable
              rows={preview.allocations}
              empty="No advance applied yet."
              columns={[
                { label: "Fee item", key: "fee_name" },
                { label: "Date", key: "transaction_date" },
                { label: "Reference", key: "reference" },
                { label: "Amount", key: "amount", money: true, right: true },
              ]}
            />
          </Section>
        </>
      )}
    </BsModal>
  );
};

export default AdvanceReconcileModal;
